import React, { useState, useEffect, useRef } from "react";
import { usePromptStore } from "../../store/promptStore";
import InputField from "./InputField";
import FieldContainer from "../static/fields/FieldContainer";
import {
  type PromptRequest,
  type PromptTypeKey,
  type LoginProvider,
  LoginProviderID,
} from "../../types/promptTypes";
import ActionButton from "./ActionButton";

type LoginFieldProps = {
  index: number;
  onEnter?: () => void;
};

const LoginField: React.FC<LoginFieldProps> = ({ index, onEnter }) => {
  const prompt = usePromptStore(
    (s) => s.prompts[index]
  ) as PromptRequest<PromptTypeKey.LOGIN>;
  const setPromptValue = usePromptStore((s) => s.setPromptValue);
  const areValuesValid = usePromptStore((s) => s.areValuesValid);

  const isSubmitting = useRef(false);

  const providers: LoginProvider[] = prompt.attributes?.providers ?? [];
  const google = providers.find((p) =>
    p.label.toLowerCase().includes("google")
  );

  const [step, setStep] = useState<"email" | "password">("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (isSubmitting.current && areValuesValid) {
      onEnter?.();
      isSubmitting.current = false;
    }
  }, [areValuesValid, onEnter]);

  const handleGoogle = () => {
    if (!google) return;
    setPromptValue(index, google);
    isSubmitting.current = true;
  };

  const handleContinue = () => {
    if (!email.trim()) return;
    setStep("password");
  };

  const handleLogin = () => {
    if (!password) return;
    const loginData = {
      id: LoginProviderID.CREDENTIALS,
      label: "Email / Password",
      email,
      password,
    };
    setPromptValue(index, loginData);
    isSubmitting.current = true;
  };

  return (
    <FieldContainer field={prompt} renderLabel={false}>
      <div className="flex flex-col">
        {step === "email" && (
          <>
            <div className="mb-5">
              <InputField
                label="Email or Username"
                value={email}
                onChange={setEmail}
                placeholder="example@email.com"
                onEnter={handleContinue}
              />
            </div>

            <ActionButton
              label="Continue"
              onClick={handleContinue}
              disabled={!email.trim()}
            />

            {google && (
              <>
                <div className="flex items-center gap-2 text-xs text-text-secondary dark:text-text-secondary-dark my-2.5">
                  <hr className="flex-grow border-gray-300 dark:border-gray-700" />
                  <span>or</span>
                  <hr className="flex-grow border-gray-300 dark:border-gray-700" />
                </div>

                <button
                  className="hover:cursor-pointer w-full border border-gray-300 dark:border-gray-600 rounded-[10px] py-3 flex items-center justify-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  onClick={handleGoogle}
                >
                  <img
                    src="/icons/google.svg"
                    alt=""
                    aria-hidden
                    className="w-5 h-5 hover:cursor-pointer"
                  />
                  <span className="text-sm font-medium hover:cursor-pointer">
                    Google
                  </span>
                </button>
              </>
            )}
          </>
        )}

        {step === "password" && (
          <div className="flex flex-col gap-5">
            <InputField
              label="Password"
              type="password"
              value={password}
              onChange={setPassword}
              placeholder="Password"
              onEnter={handleLogin}
            />

            <button
              className="w-full bg-black text-white rounded-[10px] py-3 font-medium disabled:opacity-40"
              disabled={!password}
              onClick={handleLogin}
            >
              Log in
            </button>
          </div>
        )}
      </div>
    </FieldContainer>
  );
};

export default LoginField;
